import { useEffect, useRef, useState } from 'react';

import { appendAppLog } from '../../platform/appLog';
import { meldeRueckstand } from '../header/rueckstandMelden';
import { AudioRecorder } from '../../platform/audio';
import { VideoAnnotateSheet } from '../videoannotate/VideoAnnotateSheet';
import { capturePhoto } from '../../platform/camera';
import { annotationsPfad, readBase64, readText, saveBase64 } from '../../platform/storage';
import { captureVideo } from '../../platform/video';
import { VideoThumb } from './VideoThumb';
import { overlayInnerSvg } from '../../domain/annotation';
import { markenBei } from '../../domain/videoAnnotationSvg';
import {
  ausJson,
  ebeneAus,
  type VideoOverlay,
} from '../../domain/videoAnnotation';
import type { CommentMedia, MediaKind, Task } from '../../domain/types';
import type { LogScope } from '../../platform/storage';
import { useT } from '../../i18n';
import { currentFileSubject, useAppStore } from '../../store/useAppStore';
import { formatClock } from '../../store/useTimerStore';
import { Icon } from '../../ui/Icon';
import { useToastStore } from '../../ui/Toast';
import { LoeschAufnahmeDialog, type LoeschArt } from './LoeschAufnahmeDialog';
import styles from './CommentSheet.module.css';
import { AnnotateSheet } from '../annotate/AnnotateSheet';
import { removeMediaFile } from './mediaRemove';
import { PhotoThumb } from './PhotoThumb';

/**
 * Die drei Medien-Reiter.
 *
 * **Foto** (`@capacitor/camera`) und **Audio** (eigenes Swift-Plugin) nehmen wirklich auf und
 * legen ihre Dateien unter `logs/task-comment/<art>` ab. **Video** wartet noch auf sein
 * Plugin; seine Einträge sagen „noch keine Datei", statt eine vorzutäuschen.
 */

const nowTime = () => {
  const d = new Date();
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
};

const makeId = () => Math.random().toString(36).slice(2, 10);

/**
 * Ein gescheiterter Speicherversuch — auf den Schirm **und** ins Dateilog.
 *
 * ⚠️ **Der Schirm allein genügt nicht.** Die Meldung steht neben dem Knopf und ist beim
 * nächsten Tippen weg; gemeldet wird sie uns dann aus dem Gedächtnis, ohne den Fehlercode,
 * auf den es ankommt (`OS-PLUG-FILE-00xx` sagt, welcher Aufruf scheiterte). Im Dateilog
 * bleibt sie stehen und lässt sich über den Finder herausziehen — der Documents-Ordner ist
 * freigegeben (s. `platform/storage.ts`). Befund vom 2026-09-21: Foto und Video liessen sich
 * nicht ablegen, und die einzige Spur war eine abgeschriebene halbe Zeile.
 *
 * Gibt den Text zurück, den die Oberfläche anzeigt — so steht der Aufruf an der Fehlerstelle
 * in einer Zeile.
 */
function meldeSpeicherfehler(art: string, fehler: unknown): string {
  const text = fehler instanceof Error ? fehler.message : String(fehler);
  void appendAppLog('Aufnahme (' + art + ') nicht gespeichert: ' + text, 'error').catch(() => undefined);
  return text;
}

/**
 * ⚠️ **Der Dateiname trägt die `taskId` nur beim Aufgabenkommentar.** An der Station
 * unterscheidet sie nichts und machte die Datei für `alleDateien` zu einem Aufgabenkommentar
 * (s. `persistence/repos.ts`). Die Stationskennung kommt stattdessen aus `currentFileSubject`.
 */
export const dateiZiel = (task: Task, bezug: LogScope) =>
  bezug === 'station-comment' ? { taskId: undefined } : { taskId: task.id };

function useAdd(task: Task, kind: MediaKind) {
  const addMedia = useAppStore((s) => s.addMedia);
  return (extra: Partial<CommentMedia> = {}) =>
    addMedia(task.id, kind, { id: makeId(), time: nowTime(), ...extra });
}

/**
 * Entfernt den Eintrag **und** die Datei. Ein Eintrag ohne Datei wäre ein toter Verweis,
 * eine Datei ohne Eintrag unauffindbarer Ballast im Documents-Ordner.
 *
 * Die Entscheidung „löschen, Fehlschlag melden" steckt in `removeMediaFile` (eigene, von React
 * unabhängige Datei — s. dort). Die Streichung aus dem Bogen geschieht in jedem Fall, auch wenn
 * das Löschen der Datei scheitert: der Prüfer soll nicht an einem Eintrag hängen bleiben, den er
 * gerade nicht löschen kann — die verwaiste Datei ist das kleinere Problem.
 */
function useRemove(task: Task, kind: MediaKind) {
  const removeMedia = useAppStore((s) => s.removeMedia);
  const showToast = useToastStore((s) => s.show);
  return (item: CommentMedia) => {
    void removeMediaFile(item.fileName, () => showToast('deleteFailed'));
    removeMedia(task.id, kind, item.id);
  };
}

/**
 * Die Rückfrage vor dem Wegräumen — einmal gebaut, dreimal benutzt (Ton, Foto, Video).
 *
 * Gibt den Anstoss (`frage`) und das Blatt selbst zurück. Der zu löschende Eintrag liegt
 * dazwischen im Zustand: ohne ihn wüsste das Blatt beim Bestätigen nicht mehr, **welche**
 * Aufnahme gemeint war.
 */
function useLoeschFrage(
  art: LoeschArt,
  remove: (item: CommentMedia) => void,
  beschreibe: (item: CommentMedia) => string,
) {
  const [ziel, setZiel] = useState<CommentMedia | null>(null);
  return {
    frage: (item: CommentMedia) => setZiel(item),
    dialog: ziel ? (
      <LoeschAufnahmeDialog
        art={art}
        beschreibung={beschreibe(ziel)}
        onConfirm={() => remove(ziel)}
        onClose={() => setZiel(null)}
      />
    ) : null,
  };
}

// — Audio —

export function AudioTab({ task, items, bezug = 'task-comment' }: { task: Task; items: CommentMedia[]; bezug?: LogScope }) {
  const t = useT();
  const add = useAdd(task, 'audio');
  const remove = useRemove(task, 'audio');
  const { frage, dialog } = useLoeschFrage('audio', remove, (i) =>
    t.comment.recordingAt(i.time) + ' · ' + formatClock(i.durationSeconds ?? 0));

  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!recording) return;
    const id = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [recording]);

  // ⚠️ Sicherheitsnetz: verschwindet das Blatt, während aufgenommen wird, bliebe die
  // Schlafscheibe sonst für immer angehalten — ein iPad, das nie mehr abdunkelt.
  useEffect(() => () => useAppStore.getState().setMedienAufnahme(false), []);

  const begin = async () => {
    setError(null);
    try {
      // Erst fragen, dann aufnehmen — sonst bricht die Aufnahme stumm ab und der Prüfer
      // merkt es erst, wenn nichts gespeichert wurde.
      const { granted } = await AudioRecorder.checkPermission();
      if (!granted) {
        const asked = await AudioRecorder.requestPermission();
        if (!asked.granted) {
          setError(t.comment.micDenied);
          return;
        }
      }
      await AudioRecorder.start();
      setSeconds(0);
      setRecording(true);
      // Der Schlafscheibe Bescheid sagen: während einer Aufnahme rührt niemand den
      // Bildschirm an, und sie hätte sich sonst nach fünf Minuten darübergelegt
      // (`features/schlaf/inaktivitaet.ts::sollPausieren`).
      useAppStore.getState().setMedienAufnahme(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const finish = async () => {
    setRecording(false);
    useAppStore.getState().setMedienAufnahme(false);
    try {
      const result = await AudioRecorder.stop();
      const path = await saveBase64(
        bezug,
        'audio',
        { ...currentFileSubject(), ...dateiZiel(task, bezug) },
        result.extension,
        result.base64,
      );
      add({ durationSeconds: Math.round(result.durationSeconds), fileName: path });
      void meldeRueckstand();
    } catch (e) {
      setError(meldeSpeicherfehler('Ton', e));
    } finally {
      setSeconds(0);
    }
  };

  return (
    <>
      <div className={`${styles.card} ${styles.recorder}`}>
        <button
          type="button"
          className={`tap ${styles.recButton}`}
          data-recording={recording}
          onClick={() => (recording ? void finish() : void begin())}
          aria-label={recording ? t.comment.recordStop : t.comment.recordStart}
        >
          <span className={styles.recDot} />
        </button>
        <div>
          <div className={styles.recTime}>{formatClock(seconds)}</div>
          <div className={styles.recLabel}>
            {recording ? t.comment.recordRunning : t.comment.recordReady}
          </div>
        </div>
        <Waveform live={recording} />
      </div>

      {error && <div className={styles.errorNote}>{error}</div>}

      <div className={`sc ${styles.mediaList}`}>
        {items.length === 0 && <div className={styles.emptyNote}>{t.comment.noRecording}</div>}
        {items.map((item) => (
          <AudioRow key={item.id} item={item} onRemove={() => frage(item)} />
        ))}
      </div>
      {dialog}
    </>
  );
}

/**
 * Eine Aufnahme in der Liste, mit Wiedergabe.
 *
 * Die Datei wird erst beim ersten Antippen geladen. Alle Aufnahmen einer Station vorab in den
 * Speicher zu holen, wäre auf einem Prüfungsgerät verschwenderisch.
 */
function AudioRow({ item, onRemove }: { item: CommentMedia; onRemove: () => void }) {
  const t = useT();
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const toggle = async () => {
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    if (!item.fileName) return;
    if (!audioRef.current) {
      const data = await readBase64(item.fileName);
      const type = item.fileName.endsWith('.webm') ? 'audio/webm' : 'audio/mp4';
      const element = new Audio('data:' + type + ';base64,' + data);
      element.onended = () => setPlaying(false);
      audioRef.current = element;
    }
    await audioRef.current.play();
    setPlaying(true);
  };

  useEffect(
    () => () => {
      audioRef.current?.pause();
    },
    [],
  );

  return (
    <div className={styles.mediaRow}>
      <button
        type="button"
        className={`tap ${styles.playButton}`}
        onClick={() => void toggle()}
        disabled={!item.fileName}
        aria-label={playing ? t.comment.pausePlayback : t.comment.play}
      >
        <Icon name={playing ? 'pause' : 'play'} size={15} />
      </button>
      <div className={styles.mediaText}>
        <div className={styles.mediaName}>{t.comment.recordingAt(item.time)}</div>
        <div className={styles.mediaMeta}>
          {item.fileName ? t.comment.saved : t.comment.noFile}
        </div>
      </div>
      <span className={styles.duration}>
        <Icon name="clock" size={13} />
        <span>{formatClock(item.durationSeconds ?? 0)}</span>
      </span>
      <button
        type="button"
        className={`tap ${styles.removeButton}`}
        onClick={onRemove}
        aria-label={t.comment.deleteRecording}
      >
        <Icon name="trash" size={15} />
      </button>
    </div>
  );
}

/**
 * Pegelanzeige — **kein echter Pegel**, nur eine Bewegung, die zeigt, dass aufgenommen wird.
 *
 * Das Swift-Plugin schaltet `isMeteringEnabled` zwar ein, gibt die Werte aber nicht heraus:
 * dafür müsste es im Sekundentakt Ereignisse an den Web-Teil schicken, und ein wandernder
 * Ausschlag hilft dem Prüfer kaum mehr als diese Bewegung. Die Stelle im Plugin ist
 * vorbereitet, falls es doch gebraucht wird.
 */
function Waveform({ live }: { live: boolean }) {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (!live) return;
    const id = window.setInterval(() => setPhase((p) => p + 1), 120);
    return () => window.clearInterval(id);
  }, [live]);

  return (
    <div className={styles.wave} aria-hidden="true">
      {Array.from({ length: 46 }, (_, i) => {
        const base = Math.abs(Math.sin((i + phase) * 0.7)) * 0.8 + 0.12;
        const height = Math.round((live ? base : base * 0.35) * 100);
        return (
          <span
            key={i}
            className={styles.waveBar}
            data-live={live}
            style={{ height: height + '%' }}
          />
        );
      })}
    </div>
  );
}

// — Foto —

export function PhotoTab({ task, items, bezug = 'task-comment' }: { task: Task; items: CommentMedia[]; bezug?: LogScope }) {
  const t = useT();
  const add = useAdd(task, 'photo');
  const remove = useRemove(task, 'photo');
  const { frage, dialog } = useLoeschFrage('photo', remove, (i) => t.comment.photoAt(i.time));
  const showToast = useToastStore((s) => s.show);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<CommentMedia | null>(null);
  /**
   * Zählt jedes Schliessen des Markierblatts mit — das Signal an die Kacheln, ihre Ebene neu
   * zu lesen.
   *
   * ⚠️ **Die Kachel erfährt es sonst nicht.** Das Markierblatt liegt hier als Geschwister
   * daneben, nicht über ihr; sie bleibt die ganze Zeit gemountet, und ihr `path` ändert sich
   * durch das Markieren nicht. `sichereOverlay` schreibt die Datei und lässt den Store
   * bewusst unberührt (die Ebene zählt nicht als eigene Aufnahme, s. `annotateSave.ts`) —
   * es gibt also keinen anderen Anstoss. Befund des Nutzers vom 2026-09-17: markiertes Foto,
   * unmarkierte Kachel.
   */
  const [overlayStand, setOverlayStand] = useState(0);

  const take = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await capturePhoto(bezug, {
        ...currentFileSubject(),
        ...dateiZiel(task, bezug),
      });
      if (result) {
        add({ fileName: result.path });
        // Die Kopfzeile soll die neue Datei sofort mitzählen, nicht erst beim nächsten Takt.
        void meldeRueckstand();
      }
    } catch (e) {
      // Abbruch durch den Nutzer ist der Normalfall und kein Fehler — alles andere wird gezeigt.
      const message = e instanceof Error ? e.message : String(e);
      if (!/cancel/i.test(message)) setError(meldeSpeicherfehler('Foto', e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={styles.addRow}>
        {/*
          Bewusst **nicht** gesperrt, solange die Kamera offen ist: auf dem Web-Rückfall
          (Datei-Dialog) löst ein Abbruch das Versprechen nie auf, und ein gesperrter Knopf
          bliebe dann für immer auf „öffnet …". Auf dem Gerät ist die Kamera ohnehin modal,
          ein zweiter Druck also gar nicht möglich.
        */}
        <button type="button" className={`tap ${styles.addButton}`} onClick={take}>
          <Icon name="camera" size={17} />
          <span>{busy ? t.comment.cameraOpening : t.comment.takePhoto}</span>
        </button>
        <span className={styles.addCount}>{t.comment.photoCount(items.length)}</span>
      </div>

      {error && <div className={styles.errorNote}>{t.comment.cameraUnavailable(error)}</div>}

      <div className={`sc ${styles.grid}`}>
        {items.length === 0 ? (
          <div className={styles.emptyNote}>{t.comment.noPhoto}</div>
        ) : (
          <div className={styles.gridInner}>
            {items.map((item) => (
              <div key={item.id} className={styles.photoCard}>
                <button
                  type="button"
                  className={`tap ${styles.photoButton}`}
                  onClick={() => setViewing(item)}
                  disabled={!item.fileName}
                  aria-label={t.annotate.title}
                >
                  <PhotoThumb
                    path={item.fileName}
                    alt={t.comment.photoAt(item.time)}
                    stand={overlayStand}
                  />
                </button>
                <div className={styles.photoFoot}>
                  <div className={styles.mediaText}>
                    <div className={styles.photoName}>{t.comment.photoAt(item.time)}</div>
                    <div className={styles.photoTime}>
                      {item.fileName ? t.comment.saved : t.comment.noFile}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`tap ${styles.removeButton}`}
                    onClick={() => frage(item)}
                    aria-label={t.comment.deletePhoto}
                  >
                    <Icon name="trash" size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/*
        Grossansicht **mit** Markierebene: das blosse Anschauen ist darin enthalten, wer
        nichts markiert, schliesst einfach wieder — dann entsteht auch keine Overlay-Datei
        (`sichereOverlay` schreibt nur bei einer echten Änderung).
      */}
      {viewing?.fileName && (
        <AnnotateSheet
          path={viewing.fileName}
          taskId={task.id}
          onClose={() => {
            setViewing(null);
            // Nach dem Schliessen ist die Ebene geschrieben (oder gelöscht) — beides muss die
            // Kachel sehen. Hochzählen statt eines Merkers: zwei Bearbeitungen kurz
            // hintereinander ergäben sonst ein einziges Signal.
            setOverlayStand((n) => n + 1);
          }}
          // Derselbe Toast wie bei jedem fehlgeschlagenen Schreiben auf dem Bogen — der
          // Prüfer soll nicht je nach Stelle eine andere Meldung für dieselbe Lage sehen.
          //
          // ⚠️ **Dazu die Zeile im Dateilog** (seit 2026-09-24). Vorher blieb es beim Toast, und
          // am Gerät war hinterher nicht mehr herauszufinden, **woran** das Schreiben scheiterte.
          onSaveFailed={(fehler) => {
            showToast('writeFailed');
            void appendAppLog('Markierebene sichern fehlgeschlagen: ' + String(fehler), 'error');
          }}
        />
      )}
      {dialog}
    </>
  );
}

// — Video —

export function VideoTab({ task, items, bezug = 'task-comment' }: { task: Task; items: CommentMedia[]; bezug?: LogScope }) {
  const t = useT();
  const add = useAdd(task, 'video');
  const remove = useRemove(task, 'video');
  const { frage, dialog } = useLoeschFrage('video', remove, (i) =>
    t.comment.videoAt(i.time) + ' · ' + formatClock(i.durationSeconds ?? 0));
  const showToast = useToastStore((s) => s.show);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<CommentMedia | null>(null);
  /** Welche Aufnahme gerade im Annotationseditor liegt. */
  const [markieren, setMarkieren] = useState<CommentMedia | null>(null);
  /**
   * Die gelesene Annotationsebene je Aufnahme — Dateipfad auf Ebene, `null` wenn keine da ist.
   *
   * ⚠️ **Neu geladen, sobald der Editor schliesst.** Ohne das stünde in der Liste der Stand von
   * vorher, und der Prüfer hielte seine eben gesetzte Markierung für verloren.
   *
   * ⭐ **Ein Lesevorgang, zwei Verwendungen.** Bis zum 2026-09-20 wurde die Datei hier gelesen
   * und sofort auf ihre Blockzahl eingedampft; die Kachel zeigte deshalb nie eine Markierung.
   * Jetzt bleibt die Ebene stehen: die Zahl am Markierknopf kommt aus `blocks.length`, und
   * `VideoThumb` legt die Marken des Standbildes darüber.
   */
  const [ebenen, setEbenen] = useState<Record<string, VideoOverlay | null>>({});
  /** Anteil des Zusammenschnitts, `null` solange keiner läuft. */
  const [merge, setMerge] = useState<number | null>(null);
  const videoMaxSeconds = useAppStore((s) => s.videoMaxSeconds);

  useEffect(() => {
    if (markieren) return;
    let lebt = true;
    void (async () => {
      const paare = await Promise.all(
        items
          .filter((i) => i.fileName)
          .map(async (i) => {
            const pfad = i.fileName as string;
            const text = await readText(annotationsPfad(pfad)).catch(() => null);
            /* ⚠️ **Eine unlesbare Datei ist kein Grund, die Kachel scheitern zu lassen** —
               `ebeneAus` wirft nie, eine Aufnahme ohne lesbare Ebene zeigt ihr Standbild. */
            return [pfad, ebeneAus(text)] as const;
          }),
      );
      if (lebt) setEbenen(Object.fromEntries(paare));
    })();
    return () => {
      lebt = false;
    };
  }, [items, markieren]);

  const record = async () => {
    setBusy(true);
    setError(null);
    // Auch der Zusammenschnitt nach dem Tippen auf „Fertig" gehört dazu: er dauert spürbar,
    // und der Prüfer wartet ihn ab, ohne etwas anzutippen.
    useAppStore.getState().setMedienAufnahme(true);
    try {
      const result = await captureVideo(
        bezug,
        { ...currentFileSubject(), ...dateiZiel(task, bezug) },
        videoMaxSeconds,
        // Die Vorgabe der Einrichtung (`video_audio`); umlegen kann sie der Prüfer noch in der
        // Kameraoberfläche, bevor er auslöst.
        useAppStore.getState().videoTon,
        (anteil) => setMerge(anteil),
      );
      // `null` heißt: abgebrochen. Das ist kein Fehler und bekommt keine Meldung.
      if (result) {
        add({ durationSeconds: result.durationSeconds, fileName: result.path });
        void meldeRueckstand();
      }
    } catch (e) {
      setError(meldeSpeicherfehler('Video', e));
    } finally {
      setMerge(null);
      setBusy(false);
      useAppStore.getState().setMedienAufnahme(false);
    }
  };

  return (
    <>
      <div className={styles.addRow}>
        <button type="button" className={`tap ${styles.addButton}`} onClick={() => void record()}>
          <Icon name="video" size={17} />
          <span>{busy ? t.comment.cameraOpening : t.comment.recordVideo}</span>
        </button>
        <span className={styles.addCount}>{t.comment.videoCount(items.length)}</span>
      </div>

      {error && <div className={styles.errorNote}>{error}</div>}

      {/*
        Der Zusammenschnitt nach dem Tippen auf „Fertig" dauert auf einem älteren iPad
        spürbar. Die Scheibe sperrt so lange jede Berührung — ohne sie hielte der Prüfer die
        App für hängend und tippte weiter, womöglich auf „Absenden".
      */}
      {merge !== null && (
        <div className={styles.mergePane} role="status" aria-live="polite">
          <div className={styles.mergeBox}>
            <div className={styles.mergeText}>{t.comment.mergingVideo}</div>
            <div className={styles.mergeTrack}>
              <div className={styles.mergeBar} style={{ width: Math.round(merge * 100) + '%' }} />
            </div>
            <div className={styles.mergePercent}>{Math.round(merge * 100)} %</div>
          </div>
        </div>
      )}

      <div className={`sc ${styles.mediaList}`}>
        {items.length === 0 && <div className={styles.emptyNote}>{t.comment.noVideo}</div>}
        {items.map((item) => (
          <div key={item.id} className={styles.mediaRow}>
            <button
              type="button"
              className={`tap ${styles.videoThumb}`}
              onClick={() => setViewing(item)}
              disabled={!item.fileName}
              aria-label={t.comment.playVideo}
            >
              {/* Das Vorschaubild zieht die native Seite beim Aufnehmen aus der Datei
                  (`VideoThumbnail.swift`); es liegt als `<video>-thumb.jpg` daneben. Fehlt es
                  — ältere Aufnahmen, oder das Ziehen scheiterte —, bleibt es beim Zeichen.
                  `VideoThumb` legt die Marken darüber, die im Augenblick des Standbildes
                  gelten; ohne Datei gibt es nichts zu zeigen. */}
              {item.fileName ? (
                <VideoThumb
                  datei={item.fileName}
                  ebene={ebenen[item.fileName] ?? null}
                  durationSeconds={item.durationSeconds}
                />
              ) : (
                <PhotoThumb alt="" platzhalter="video" />
              )}
              <span className={styles.videoPlay}>
                <Icon name="play" size={20} />
              </span>
            </button>
            <div className={styles.mediaText}>
              <div className={styles.mediaName}>{t.comment.videoAt(item.time)}</div>
              <div className={styles.mediaMeta}>
                {item.fileName ? t.comment.saved : t.comment.noFile}
              </div>
            </div>
            <span className={styles.duration}>
              <Icon name="clock" size={13} />
              <span>{formatClock(item.durationSeconds ?? 0)}</span>
            </span>
            {/* Der Weg in den Annotationseditor. Ohne Datei gibt es nichts zu markieren. */}
            <button
              type="button"
              className={`tap ${styles.removeButton} ${styles.markButton}`}
              onClick={() => setMarkieren(item)}
              disabled={!item.fileName}
              aria-label={t.videoAnnotate.open}
            >
              <Icon name="markArrow" size={15} />
              {/* Die Zahl der Blöcke sagt auf einen Blick, ob die Aufnahme markiert ist.
                  Keine Zahl heisst: noch nichts darauf. */}
              {(ebenen[item.fileName ?? '']?.blocks.length ?? 0) > 0 && (
                <span className={styles.markCount}>
                  {ebenen[item.fileName ?? '']?.blocks.length}
                </span>
              )}
            </button>
            <button
              type="button"
              className={`tap ${styles.removeButton}`}
              onClick={() => frage(item)}
              aria-label={t.comment.deleteVideo}
            >
              <Icon name="trash" size={15} />
            </button>
          </div>
        ))}
      </div>

      {viewing && <VideoViewer item={viewing} onClose={() => setViewing(null)} />}
      {markieren?.fileName && (
        <VideoAnnotateSheet
          path={markieren.fileName}
          taskId={task.id}
          onClose={() => setMarkieren(null)}
          /* ⬅️ **Fehlte bis zum 2026-09-24.** Scheiterte das Sichern der Videoebene, geschah
             nichts: kein Toast, keine Zeile im Log — dieselbe Lage wie beim Foto daneben, nur
             lautlos. */
          onSaveFailed={(fehler) => {
            showToast('writeFailed');
            void appendAppLog('Video-Ebene sichern fehlgeschlagen: ' + String(fehler), 'error');
          }}
        />
      )}
      {dialog}
    </>
  );
}

/**
 * Video in groß — **mit seinen Annotationen**, wie ein Foto mit seiner Markierebene.
 *
 * Es wird als Datenadresse geladen: bei einer Aufnahme von wenigen Minuten vertretbar, weil
 * sie einmal durchläuft. (Der Editor nimmt stattdessen `abspielAdresse`, weil dort ständig
 * gesprungen wird.)
 */
function VideoViewer({ item, onClose }: { item: CommentMedia; onClose: () => void }) {
  const t = useT();
  const [src, setSrc] = useState<string | null>(null);
  const [ebene, setEbene] = useState<VideoOverlay | null>(null);
  const [positionMs, setPositionMs] = useState(0);
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!item.fileName) return;
    let alive = true;
    readBase64(item.fileName)
      .then((data) => {
        if (!alive || !data) return;
        const type = item.fileName?.endsWith('.mp4') ? 'video/mp4' : 'video/quicktime';
        setSrc('data:' + type + ';base64,' + data);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [item.fileName]);

  /* Die Annotationsebene daneben. Fehlt oder bricht sie, läuft das Video ohne — hier wird nur
     angesehen, und ein Lesefehler soll die Wiedergabe nicht verhindern. */
  useEffect(() => {
    if (!item.fileName) return;
    let alive = true;
    readText(annotationsPfad(item.fileName))
      .then((text) => {
        if (!alive || !text) return;
        setEbene(ausJson(text));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [item.fileName]);

  /*
   * ⚠️ **`requestAnimationFrame`, nicht `timeupdate`** — dieselbe Begründung wie im Editor:
   * `timeupdate` feuert nur etwa alle 250 ms, und eine Marke hinge nach dem Ende ihres Bandes
   * sichtbar nach.
   */
  useEffect(() => {
    if (!ebene) return;
    let angefordert = 0;
    const takt = () => {
      const el = video.current;
      if (el) setPositionMs(Math.round(el.currentTime * 1000));
      angefordert = requestAnimationFrame(takt);
    };
    angefordert = requestAnimationFrame(takt);
    return () => cancelAnimationFrame(angefordert);
  }, [ebene]);

  /* Dieselbe Stapelregel wie Kachel und Prüfvektoren — sie steht in `markenBei`, nicht hier.
     Drei Abschriften derselben Reihenfolge wären drei Gelegenheiten, sie zu verlieren. */
  const marken = ebene ? markenBei(ebene, positionMs) : [];

  return (
    <div className={styles.viewer} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.viewerInner} onClick={(e) => e.stopPropagation()}>
        {src ? (
          <div className={styles.viewerBuehne}>
            {/*
              ⚠️ **`playsInline` ist Pflicht, sobald etwas darüberliegt.** Ohne das reisst iOS
              das Video in den nativen Vollbildspieler, und die Ebene läge dahinter — genau der
              Grund, warum hier bisher keine Annotationen zu sehen waren.
            */}
            {/*
              ⚠️ **Stumm beim Öffnen** (Vorgabe des Nutzers, 2026-09-21). Ein Prüfer, der eine
              Aufnahme nur nachsehen will, soll nicht versehentlich die Station nebenan
              beschallen. Den Ton holt er über die Steuerleiste, die iOS hier ohnehin zeigt.

              ⚠️ **`muted` muss zusätzlich am Element gesetzt werden.** React schreibt das
              Attribut nicht ins Markup; allein über die Eigenschaft startet iOS mit Ton
              (deshalb das `ref` unten, nicht nur die Prop).
            */}
            <video
              ref={(el) => {
                video.current = el;
                if (el) el.muted = true;
              }}
              className={styles.viewerVideo}
              src={src}
              controls
              playsInline
              autoPlay
              muted
            />
            {ebene && marken.length > 0 && (
              /* ⚠️ **Nicht anfassbar.** Die Ebene deckt das Video vollständig ab; nähme sie
                 Tipper an, käme niemand mehr an die Steuerleiste darunter. */
              <svg
                className={styles.viewerEbene}
                viewBox={`0 0 ${ebene.width} ${ebene.height}`}
                dangerouslySetInnerHTML={{
                  __html: overlayInnerSvg({
                    width: ebene.width,
                    height: ebene.height,
                    markers: marken,
                  }),
                }}
              />
            )}
          </div>
        ) : (
          <span className={styles.placeholder}>
            <Icon name="clock" size={22} />
          </span>
        )}
      </div>
      <button type="button" className={`tap ${styles.viewerClose}`} onClick={onClose}>
        <Icon name="close" size={18} />
        <span>{t.comment.close}</span>
      </button>
    </div>
  );
}
